import { CommonModule } from '@angular/common';
import { Component, Inject, ElementRef, HostListener, ViewChild } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ImageCropperComponent, ImageCroppedEvent } from 'ngx-image-cropper';
import { DomSanitizer } from '@angular/platform-browser';

@Component({
	selector: "app-feature-not-available-dialog",
	templateUrl: "./feature-not-available.dialog.html",
	standalone: false
})
export class FeatureNotAvailableDialog {
	close(e: any): void {
		this.dialogRef.close();
	}

	constructor(
		public dialogRef: MatDialogRef<FeatureNotAvailableDialog>) {
	}
}


@Component({
	selector: "app-read-more-dialog",
	templateUrl: "./read-more.dialog.html",
	standalone: false
})
export class ReadMoreDialog {
	title: string;
	description: string;
	objectURL: string;

	constructor(
		public dialogRef: MatDialogRef<ReadMoreDialog>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		this.title = data?.title ?? "";
		this.description = data?.description ?? "";
		this.objectURL = data?.objectURL ?? "";
	}

	close(e: any): void {
		this.dialogRef.close();
	}

	go(url: any) {
		window.open(url);
	}
}


@Component({
	selector: "app-feature-not-available-2b-dialog",
	templateUrl: "./feature-not-available-2b.dialog.html",
	standalone: false
})
export class FeatureNotAvailable2BDialog {
	img: string;

	constructor(
		public dialogRef: MatDialogRef<ReadMoreDialog>,
		@Inject(MAT_DIALOG_DATA) public data: any
	) {
		this.img = data.img;
		this.img = this.img ?? "assets/images/connections-followers-favorites-lg.png";
	}

	close(e: any): void {
		this.dialogRef.close();
	}
}


//https://www.bennadel.com/blog/3831-hiding-overflow-scrollbars-until-hover-ala-gmail-labels-in-angular-9-1-7.htm
@Component({
	selector: "app-scroll-on-hover",
	host: {
		"[class.is-scrollable]": "isScrollable",
		"(mouseenter)": "enable()",
		"(mouseleave)": "disable()"
	},
	styles: [
		`
			:host {display: block;	overflow: hidden;}
			:host( .is-scrollable ) {overflow: auto; overscroll-behavior: contain;}
			.wrapper {width: calc( 100% - 16px );}
		`
	],
	template:
		`
		<div class="wrapper" [style.width.px]="wrapperWidth">
			<ng-content></ng-content>
		</div>
	`,
	standalone: false
})
export class ScrollOnHoverComponent {
	public isScrollable: boolean;
	public wrapperWidth: number | null;
	private elementRef: ElementRef;

	// I initialize the scroll-on-mouseenter component.
	constructor(elementRef: ElementRef) {
		this.elementRef = elementRef;
		this.isScrollable = false;
		this.wrapperWidth = null;
	}

	// I disable scrolling on the host component.
	public disable(): void {
		this.isScrollable = false;
		this.wrapperWidth = null;
	}

	// I enable scrolling on the host component.
	public enable(): void {
		this.isScrollable = true;
		this.wrapperWidth = this.elementRef.nativeElement.childNodes[0].clientWidth;
	}
}


export type CropperType = "circle" | "rectangle" | "square";
@Component({
	selector: "app-image-cropper",
	templateUrl: "./image-cropper.dialog.html",
	styles: [`
		.crop-option-col {
			text-align: right;
			margin-top: 20px;
			margin-right: 30px;
			margin-left: -30px;
		}
		.crop-option-col img {
			width: 20px;
			height: auto;
			margin-top: -5px;
		}
		.crop-option-col img:nth-child(2) {
			width: 16px;
		}
	`],
	standalone: true,
	imports: [ImageCropperComponent, CommonModule ]
})
export class ImageCropperDialog {
	imgType: CropperType;
	imageChangedEvent: any = null;
	selectedImage: any = "";
	imageWidth: number = 0;
	imageHeight: number = 0;
	clientWidth: number = 0;
	clientHeight: number = 0;

	@ViewChild("imageCropper") imageCropper: ImageCropperComponent;
	modalSize = { width: 600, height: 450 };
	canvasSize = { width: 600, height: 300 };
	aspectRatio: number = 1;
	roundCropper: boolean = false;
	resizeToWidth: number = 0;
	resizeToHeight: number = 0;
	croppedImage: any = null;

	constructor(
		private dialogRef: MatDialogRef<ImageCropperDialog>,
		@Inject(MAT_DIALOG_DATA) private data: any,
		private sanitizer: DomSanitizer
	) {
		this.imgType = data.imgType;
		this.selectedImage = data.selectedImage;

		if (this.imgType === "circle") {
			this.modalSize = { width: 600, height: 450 };
			this.canvasSize = { width: 300, height: 300 };
			this.aspectRatio = 1;
			this.roundCropper = true;
			this.resizeToWidth = 230;
			this.resizeToHeight = 230;
		}
		else if (this.imgType === "square") {
			this.modalSize = { width: 600, height: 450 };
			this.canvasSize = { width: 300, height: 300 };
			this.aspectRatio = 1;
			this.roundCropper = false;
			this.resizeToWidth = 200;
			this.resizeToHeight = 200;
		}
		else {
			this.modalSize = { width: 600, height: 450 };
			this.canvasSize = { width: 520, height: 260 };
			this.aspectRatio = 2;
			this.roundCropper = false;
			this.resizeToWidth = 1200;
			this.resizeToHeight = 600;
		}
	}

	private getImageSize(): Promise<any> {
		return new Promise((resolve, reject) => {
			let image = new Image() as HTMLImageElement;
			image.src = this.selectedImage.toString();
			image.onload = ($event) => {
				this.imageHeight = ($event.currentTarget as HTMLImageElement).height;
				this.imageWidth = ($event.currentTarget as HTMLImageElement).width;
				resolve({
					height: this.imageHeight,
					width: this.imageWidth
				});
			}
		});
	}

	private setDisplay(): void {
		if (window.innerWidth <= this.modalSize.width) {
			this.modalSize.width = window.innerWidth - 20;
			this.canvasSize.width = this.modalSize.width;
		}
		if (window.innerHeight <= this.modalSize.height) {
			this.modalSize.height = window.innerHeight - 10;
			this.canvasSize.height = this.modalSize.height;
		}
	}

	imageCropped(event: ImageCroppedEvent) {
		this.croppedImage = event.base64;
	}

	imageLoaded() {
		// show cropper
		this.clientHeight = this.imageCropper.sourceImage.nativeElement.clientHeight;
		this.clientWidth = this.imageCropper.sourceImage.nativeElement.clientWidth;
		if (window.innerHeight + 150 <= this.clientHeight) {
			this.modalSize.height = window.innerHeight - 20;
			this.canvasSize.height = window.innerHeight - 170;
		}
		else {
			this.modalSize.height = this.clientHeight + 150;
			this.canvasSize.height = this.clientHeight;
		}
	}

	cropperReady() {
		// cropper ready
	}

	loadImageFailed() {
		this.dialogRef.close(null);
	}

	save() {
		this.dialogRef.close(this.croppedImage);
	}

	cancel() {
		this.dialogRef.close(null);
	}

	isRotating: boolean = false;
	rotate(isClockwise: any) {
		if (!this.selectedImage) {
			return;
		}
		this.isRotating = true;
		this.rotateBase64Image(
			this.selectedImage,
			isClockwise,
			(result: any) => {
				this.selectedImage = result;
				this.getImageSize();
				this.isRotating = false;
			}
		);
	}

	private rotateBase64Image(base64data: any, isClockwise: any, callback: any): void {
		var image = new Image();
		image.src = base64data;
		image.onload = function () {
			let canvas = document.createElement('canvas');
			canvas.width = image.height;
			canvas.height = image.width;
			let ctx = canvas.getContext("2d");
			let deg = isClockwise ? Math.PI / 2 : Math.PI / -2;
			ctx.translate(canvas.width / 2, canvas.height / 2);
			ctx.rotate(deg);
			ctx.drawImage(image, -image.width / 2, -image.height / 2);
			ctx.rotate(-deg);
			ctx.translate(-canvas.width / 2, -canvas.height / 2);
			callback(canvas.toDataURL());
		};
	}


	@HostListener('window:resize', ['$event'])
	onResize(e: any): void {
		this.setDisplay();
	}

	ngOnInit(): void {
		this.getImageSize();
		this.setDisplay();
	}
}

